import { CalendarRange } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import {
  invalidRangeReason,
  isUsable,
  isoDaysAgo,
  todayIso,
  type DateRange,
  type DateRangePreset,
} from "./dateRange";

export type PresetOption = { value: DateRangePreset; label: string };

/**
 * Preset buttons plus a custom from/to window, shared by the analytics pages.
 *
 * Native date inputs rather than a calendar widget: the CRM has no calendar
 * primitive and this needs no dependency to be keyboard-accessible, localized
 * by the browser, and typable — which is what an analyst comparing two known
 * dates actually wants.
 */
export function DateRangePicker({
  label = "Range",
  presets,
  value,
  onChange,
  maxDate = todayIso(),
  note,
}: {
  label?: string;
  presets: ReadonlyArray<PresetOption>;
  value: DateRange;
  onChange: (value: DateRange) => void;
  /** Latest selectable day; defaults to today (no future analytics exist). */
  maxDate?: string;
  /** Rendered inside the popover — e.g. a page's week-rounding caveat. */
  note?: string;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => draftFrom(value));

  // Reopening after a preset was picked elsewhere should not show a stale draft.
  useEffect(() => {
    if (open) setDraft(draftFrom(value));
  }, [open, value]);

  const draftRange: DateRange = { kind: "custom", ...draft };
  const draftValid = isUsable(draftRange);
  const isCustom = value.kind === "custom";

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="flex rounded-md border">
        {presets.map((option) => (
          <Button
            key={option.value}
            type="button"
            size="sm"
            variant={
              !isCustom && value.preset === option.value ? "secondary" : "ghost"
            }
            className="rounded-none first:rounded-l-md"
            onClick={() => onChange({ kind: "preset", preset: option.value })}
          >
            {option.label}
          </Button>
        ))}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              size="sm"
              variant={isCustom ? "secondary" : "ghost"}
              className="rounded-none last:rounded-r-md"
              aria-label="Custom date range"
            >
              <CalendarRange className="size-3.5" />
              {isCustom ? `${value.from} → ${value.to}` : "Custom"}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 space-y-3">
            <div className="space-y-1">
              <Label htmlFor="date-range-from" className="text-xs">
                From
              </Label>
              <Input
                id="date-range-from"
                type="date"
                value={draft.from}
                max={draft.to || maxDate}
                onChange={(event) =>
                  setDraft((prev) => ({ ...prev, from: event.target.value }))
                }
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="date-range-to" className="text-xs">
                To
              </Label>
              <Input
                id="date-range-to"
                type="date"
                value={draft.to}
                min={draft.from}
                max={maxDate}
                onChange={(event) =>
                  setDraft((prev) => ({ ...prev, to: event.target.value }))
                }
              />
            </div>
            {!draftValid && (draft.from || draft.to) && (
              <p className="text-xs text-destructive" role="alert">
                {invalidRangeReason(draft)}
              </p>
            )}
            {note && <p className="text-xs text-muted-foreground">{note}</p>}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={!draftValid}
                onClick={() => {
                  onChange(draftRange);
                  setOpen(false);
                }}
              >
                Apply
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

function draftFrom(value: DateRange): { from: string; to: string } {
  if (value.kind === "custom") return { from: value.from, to: value.to };
  // Seed a custom draft with the last 30 days so the picker opens on something
  // sensible rather than two empty fields.
  return { from: isoDaysAgo(30), to: todayIso() };
}
